'use client';

import type { FC } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { pluralize } from '../../i18n/pluralize';
import { rarityGradient } from '../../utils/rarityGradient';
import { Credits } from '../Credits';
import { Toast, ToastTile } from '../Toast';
import { BlurImage } from '../ui/BlurImage';
import { SkinImage } from '../ui/SkinImage';
import translations from './RainToast.translations';
import type { RainCase, RainItem } from './types';

interface CommonProps {
  onClick?: () => void;
  onDismiss?: () => void;
}

interface StartProps extends CommonProps {
  kind: 'start';
  sourceCase: RainCase;
  minutesLeft: number;
}

interface WinProps extends CommonProps {
  kind: 'win';
  prize: RainItem;
}

export type RainToastProps = StartProps | WinProps;

export const RainToast: FC<RainToastProps> = (props) => {
  const { t, lang } = useLocalTranslation(translations);

  if (props.kind === 'start') {
    const { sourceCase, minutesLeft, onClick, onDismiss } = props;
    const timeLeft =
      minutesLeft < 1
        ? t('lastMinute')
        : pluralize(
            minutesLeft,
            {
              one: t('minutesOne', { count: minutesLeft }),
              few: t('minutesFew', { count: minutesLeft }),
              many: t('minutesMany', { count: minutesLeft }),
            },
            lang,
          );

    return (
      <Toast
        leading={
          <ToastTile>
            <BlurImage
              src={sourceCase.imageUrl}
              placeholder={sourceCase.imagePlaceholder}
              alt={sourceCase.name}
              className="h-full w-full"
            />
          </ToastTile>
        }
        overline={t('startOverline')}
        title={sourceCase.name}
        description={timeLeft}
        accent="blue"
        onClick={onClick}
        onDismiss={onDismiss}
      />
    );
  }

  const { prize, onClick, onDismiss } = props;

  return (
    <Toast
      leading={
        <ToastTile>
          <div className="absolute inset-0" style={{ background: rarityGradient(prize.rarityColor) }} />
          <SkinImage
            name={prize.name}
            src={prize.imageUrl}
            className="absolute inset-0"
            imgClassName="h-full w-full object-contain p-0.5"
          />
        </ToastTile>
      }
      overline={t('winOverline')}
      title={prize.name}
      description={<Credits amount={prize.priceInCredits} iconSize={11} />}
      accent="amber"
      onClick={onClick}
      onDismiss={onDismiss}
    />
  );
};
